import { createBlocksClient } from "@seliseblocks/client";
import { BLOCKS_API_URL, PROJECT_KEY } from "../../blocks/config";
import apiClient from "../axios";

/**
 * Client for the `club-groups-v1` Function, the only writer of group
 * memberships once the backend lockdown is on. The server decides "private"
 * and "group admin" and keeps every group post's `memberIds` in step.
 *
 * Unset EXPO_PUBLIC_GROUPS_FUNCTION_ID keeps the legacy direct writes, so the
 * app can ship before the backend switch and keep working either way.
 */
const groupsId = process.env.EXPO_PUBLIC_GROUPS_FUNCTION_ID ?? "";
export const groupsFunctionEnabled = !!groupsId;

export type GroupSnapshot = {
  firstName?: string;
  lastName?: string;
  email?: string;
  profileImageUrl?: string | null;
};
export type GroupsAction =
  | { action: "initGroup"; groupId: string; snapshot?: GroupSnapshot; members?: { userId: string; snapshot?: GroupSnapshot }[] }
  | { action: "join"; groupId: string; snapshot?: GroupSnapshot }
  | { action: "leave"; groupId: string }
  | { action: "approve" | "reject" | "remove"; membershipId: string }
  | { action: "addMembers"; groupId: string; members: { userId: string; snapshot?: GroupSnapshot }[] }
  | { action: "setRole"; membershipId: string; role: "Admin" | "Member" }
  | { action: "deleteGroup"; groupId: string }
  | { action: "refreshSnapshot"; snapshot: GroupSnapshot }
  | { action: "stampPost"; postId: string };

export class GroupsFunctionError extends Error {
  constructor(public code: string, public status: number) {
    super(code);
  }
}

const makeClient = () =>
  createBlocksClient({
    apiUrl: BLOCKS_API_URL,
    xBlocksKey: PROJECT_KEY,
    fetch: async (url, init) => {
      const response = await apiClient.request({
        url: String(url), method: init?.method, data: init?.body,
        headers: Object.fromEntries(new Headers(init?.headers).entries()),
        timeout: 30000, validateStatus: (status) => status !== 401,
      });
      return new Response(JSON.stringify(response.data), {
        status: response.status, headers: { "content-type": "application/json" },
      });
    },
  });

const unwrap = (value: any) => {
  if (value && typeof value.statusCode === "number") {
    if (value.statusCode >= 400) throw new GroupsFunctionError(value.body?.code ?? "GROUPS_UNAVAILABLE", value.statusCode);
    return value.body;
  }
  return value;
};

export async function callGroups<T = any>(body: GroupsAction): Promise<T> {
  if (!groupsId) throw new GroupsFunctionError("GROUPS_FUNCTION_NOT_CONFIGURED", 503);
  const blocks = makeClient();
  let value: any;
  try {
    value = await blocks.http.request(`/logic/v4/fn/${encodeURIComponent(groupsId)}`, {
      method: "POST", body, headers: { Prefer: "wait=25" },
    });
  } catch (error: any) {
    throw new GroupsFunctionError(error?.body?.code ?? "GROUPS_UNAVAILABLE", error?.status ?? 503);
  }
  if (value?.runId && value?.pollToken) {
    const deadline = Date.now() + 30000;
    for (let delay = 500; Date.now() < deadline; delay = Math.min(delay * 1.5, 4000)) {
      await new Promise((resolve) => setTimeout(resolve, delay));
      const result: any = await blocks.http.request(`/logic/v4/fn/runs/${encodeURIComponent(value.runId)}/result`, {
        headers: { "x-poll-token": value.pollToken },
      });
      const status = String(result.status).toUpperCase();
      if (status === "SUCCEEDED") return unwrap(typeof result.result === "string" ? JSON.parse(result.result) : result.result) as T;
      if (["FAILED", "TIMEDOUT", "TIMED_OUT", "CANCELLED", "CANCELED"].includes(status)) throw new GroupsFunctionError("GROUPS_FAILED", 503);
    }
    throw new GroupsFunctionError("GROUPS_TIMEOUT", 504);
  }
  return unwrap(value) as T;
}

/** Fail-closed audience stamp: a missed stamp leaves the post visible to its author only. */
export async function stampGroupPost(postId: string | undefined, visibility: string | undefined) {
  if (!groupsFunctionEnabled || !postId || visibility !== "group") return;
  try {
    await callGroups({ action: "stampPost", postId });
  } catch {
    // The server sweep (reconcile) repairs it; the author still sees the post.
  }
}
